<template lang="pug">
UpsertPage(
  title="Funnel settings"
  @save="validateAndSave"
  save-label="Save settings"
  :pending="isSaving"
  hide-cancel
  :showAuditTrail="canViewAuditTrail"
  objectType="BusinessUnit",
  :objectKey="settings.BusinessUnitKey"
  )
  div
    BriqPanel
      BriqMultilanguageWarningModal(
        v-if="hasMultipleLanguages"
        v-show="showWarningModal"
        :defaultModel="settings"
        :translationInfo="settings.TranslationInfo"
        :translatableProperties="translatableProperties"
        @close="showWarningModal = false" 
        @onSubmit="save"
        )
      .funnel-settings-header
        BriqButton.translation-button(
          v-if="hasMultipleLanguages"
          small
          variant="secondary"
          @click="toggleTranslation"
          data-test-id="translation-button"
          ) 
            .flex
              .translation-icon.mr-XXS
                BriqIcon(:name="`icon-input-${toggleTranslationIcon}-icon`") 
              .translation-text {{ toggleTranslationText }}
      BriqMultilanguage(
        v-if="hasMultipleLanguages"
        v-model="activeCulture"
        :translationFields="translationFields"
        :cultures="availableCultures"
        :showSecondColumn="shouldShowSecondColumn"
        :defaultCulture="defaultCultureDisplayName"
        )
        FunnelSettingsFields(
          :settings="settings"
          :displayAdvanced="displayAdvanced"
          :isSaving="isSaving"
          :validations="$v"
          )
        template(v-slot:secondary)
          FunnelSettingsFields(
            :settings="translationModel"
            :displayAdvanced="displayAdvanced"
            :isSaving="isSaving"
            )
      FunnelSettingsFields(
        v-else
        :settings="settings"
        :displayAdvanced="displayAdvanced"
        :isSaving="isSaving"
        :validations="$v"
        )

</template>

<script>
import UpsertPage from '@v/components/UpsertPage.vue'
import BriqMultilanguage from '@v/components/BriqMultilanguage.vue'
import FunnelSettingsFields from './FunnelSettingsFields.vue'
import { decimal, minValue, maxValue } from 'vuelidate/lib/validators'
import completeTranslationInfo from '@/vue/util/completeTranslationInfo.js'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'

export default {
  name: 'FunnelSettings',
  components: {
    UpsertPage,
    BriqMultilanguage,
    FunnelSettingsFields,
    BriqMultilanguageWarningModal,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    isSaving: Boolean,
    displayAdvanced: Boolean,
    enableMultilanguage: Boolean, // feature flag
    canViewAuditTrail: Boolean,
    enableCustomDomain: Boolean, // feature flag
  },
  data() {
    return {
      activeCulture: null,
      showSecondColumn: false,
      showWarningModal: false,
    }
  },
  validations: {
    settings: {
      FacebookRating: {
        decimal,
        minValue: minValue(0),
        maxValue: maxValue(5),
      },
      TripadvisorRating: {
        decimal,
        minValue: minValue(0),
        maxValue: maxValue(5),
      },
    },
  },
  computed: {
    toggleTranslationText() {
      const what = this.showSecondColumn ? 'Hide' : 'Show'
      return what + ' translations'
    },
    toggleTranslationIcon() {
      return this.showSecondColumn ? 'eye-crossed' : 'eye-open'
    },
    hasMultipleLanguages() {
      return this.enableMultilanguage && !!this.settings.TranslationInfo?.AvailableCultures?.length
    },
    shouldShowSecondColumn() {
      return this.hasMultipleLanguages && this.showSecondColumn
    },
    availableCultures() {
      return this.settings.TranslationInfo?.AvailableCultures?.filter(
        c => c.Name !== this.defaultCulture?.Name,
      )
    },
    defaultCulture() {
      return this.settings.TranslationInfo?.DefaultCulture
    },
    translationModel: {
      get() {
        if (!this.hasMultipleLanguages || !this.activeCulture) return {}
        return this.settings.TranslationInfo.Translations[this.activeCulture.Name]
      },
      set() {},
    },
    translationFields() {
      return ['Unique selling points', 'Browser tagline']
    },
    translatableProperties() {
      return [
        {
          label: 'USPs',
          key: 'Usps',
        },
        {
          label: 'Tagline (shown in title)',
          key: 'Tagline',
        },
      ]
    },
    defaultCultureDisplayName() {
      return this.defaultCulture?.DisplayName
    },
  },
  methods: {
    validateAndSave() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        if (
          this.hasMultipleLanguages &&
          emptyTranslatableFieldsExist(
            this.settings,
            this.settings.TranslationInfo,
            this.translatableProperties,
          )
        ) {
          this.showWarningModal = true
        } else this.save()
      } else this.$notifyError('There are errors in the form that need to be resolved first.')
    },
    save() {
      this.$emit('save')
    },
    toggleTranslation() {
      this.showSecondColumn = !this.showSecondColumn
    },
  },
  beforeMount() {
    if (!this.hasMultipleLanguages) return
    this.activeCulture = this.availableCultures?.[0]
    completeTranslationInfo(this.settings)
  },
}
</script>

<style lang="stylus" scoped>
.funnel-settings-header
  display flex
  flex-direction row-reverse
  margin-bottom 20px
</style>
